'use client';

import { Loader, LoaderProps } from './Loader';

/**
 * Suspense Loader Component
 * Used as fallback for all Suspense boundaries
 * Uses theme-aware loader when available
 */

interface SuspenseLoaderProps extends Omit<LoaderProps, 'fullScreen'> {
  /**
   * Type of content being loaded
   */
  type?: 'component' | 'page' | 'config' | 'theme';

  /**
   * Optional custom message based on type
   */
  message?: string;
}

const defaultMessages = {
  component: 'Loading component...',
  page: 'Loading page...',
  config: 'Loading configuration...',
  theme: 'Loading theme...',
};

/**
 * Suspense fallback loader
 * Note: Cannot use hooks here as it might render before providers are ready
 */
export function SuspenseLoader({ type = 'component', message, size = 'md', ...props }: SuspenseLoaderProps) {
  const loaderMessage = message || defaultMessages[type];

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: type === 'page' ? '100vh' : '200px',
        width: '100%',
      }}
    >
      <Loader size={size} message={loaderMessage} {...props} />
    </div>
  );
}

/**
 * Config-specific loader
 */
export function ConfigLoader({ message, ...props }: Omit<SuspenseLoaderProps, 'type'>) {
  return <SuspenseLoader type="config" message={message || 'Loading configuration...'} {...props} />;
}

/**
 * Theme-specific loader
 */
export function ThemeLoader({ message, ...props }: Omit<SuspenseLoaderProps, 'type'>) {
  return (
    <SuspenseLoader
      type="theme"
      // message={message || 'Loading theme...'}
      message={message||''}
      {...props}
    />
  );
}

/**
 * Page-specific loader (full screen)
 */
export function PageLoader({ message, ...props }: Omit<SuspenseLoaderProps, 'type'>) {
  return <SuspenseLoader type="page" message={message || 'Loading page...'} size="lg" {...props} />;
}

export default SuspenseLoader;
